import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart,
  Sparkles,
  Scissors,
  Eye,
  Home,
  Store,
  Gift,
  Copy,
  CheckCircle,
  Loader2,
  Send,
  Phone,
  Users,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const comboPackages = [
  {
    id: "bridal_hair_makeup",
    title: "Bridal Hair & Makeup",
    description: "Includes premium makeup, hair updo or styling, and lashes.",
    price: "Contact Us",
    forWho: "Bride",
    highlight: true,
  },
  {
    id: "bridesmaid_hair_makeup",
    title: "Hair & Makeup",
    description: "Includes makeup, and hair updo or styling for bridesmaids or guests.",
    price: "Contact Us",
    forWho: "Bridesmaids / Guests",
  },
  {
    id: "mother_hair_makeup",
    title: "Mother Hair & Makeup",
    description: "Includes makeup, and hair updo or styling.",
    price: "Contact Us",
    forWho: "Mother of Bride / Groom",
  },
  {
    id: "trial_hair_makeup",
    title: "Trial Hair & Makeup",
    description: "Includes premium makeup, hair updo or styling, and lashes.",
    price: "Contact Us",
    forWho: "Bride (Trial Run)",
  },
];

const hairOnlyPackages = [
  {
    id: "hair_bride",
    title: "Hair for Bride",
    description: "Hair updo or styling service for the bride.",
    price: "Contact Us",
  },
  {
    id: "hair_bridesmaid",
    title: "Hair for Bridesmaid",
    description: "Hair updo or styling service for bridesmaid or guests.",
    price: "Contact Us",
  },
  {
    id: "hair_mother",
    title: "Hair for Mother",
    description: "Hair updo or styling service for the mother of bride or groom.",
    price: "Contact Us",
  },
];

const makeupOnlyPackages = [
  {
    id: "makeup_bride",
    title: "Makeup for Bride",
    description: "Premium makeup service for the bride.",
    price: "Contact Us",
  },
  {
    id: "makeup_bridesmaid",
    title: "Makeup for Bridesmaid",
    description: "Makeup service for bridesmaid or guests.",
    price: "Contact Us",
  },
  {
    id: "makeup_mother",
    title: "Makeup for Mother",
    description: "Makeup service for the mother of bride or groom.",
    price: "Contact Us",
  },
];

const PackageCard = ({ pkg, onInquire }) => (
  <div className={`bento-card p-6 relative ${pkg.highlight ? "border-[#C5A059] ring-1 ring-[#C5A059]/20" : ""}`}
    data-testid={`wedding-pkg-${pkg.id}`}>
    {pkg.highlight && (
      <div className="absolute -top-3 left-6 bg-[#C5A059] text-[#050505] text-[10px] font-bold tracking-wider uppercase px-3 py-1">
        Most Popular
      </div>
    )}
    {pkg.forWho && (
      <span className="text-[10px] font-semibold tracking-wider uppercase text-[#C5A059] mb-2 block">
        {pkg.forWho}
      </span>
    )}
    <h3 className="font-heading text-lg text-white mb-2">{pkg.title}</h3>
    <p className="font-body text-sm text-[#A3A3A3] mb-4 leading-relaxed">{pkg.description}</p>
    <div className="flex items-center justify-between">
      <span className="font-heading text-lg text-[#C5A059]">{pkg.price}</span>
      <button
        onClick={() => onInquire(pkg.title)}
        className="bg-[#C5A059] text-[#050505] font-semibold text-[10px] tracking-[0.15em] uppercase px-4 py-2 hover:brightness-110 transition-all"
      >
        Inquire
      </button>
    </div>
  </div>
);

export const WeddingPackagePage = () => {
  const [showInquiry, setShowInquiry] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [serviceLocation, setServiceLocation] = useState("in_store");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bridalCode, setBridalCode] = useState(null);
  const [form, setForm] = useState({
    bride_name: "",
    partner_name: "",
    phone: "",
    email: "",
    wedding_date: "",
    party_size: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openInquiry = (pkgTitle) => {
    setSelectedPackage(pkgTitle);
    setShowInquiry(true);
    setTimeout(() => {
      document.getElementById("wedding-inquiry")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        selected_package: selectedPackage,
        service_location: serviceLocation,
      };
      const res = await axios.post(`${API}/wedding-inquiries`, payload);
      if (res.data.success) {
        setBridalCode(res.data.bridal_code);
        setSubmitted(true);
        toast.success("Wedding inquiry submitted!");
      }
    } catch {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(bridalCode || "");
    toast.success("Bridal code copied!");
  };

  return (
    <div data-testid="wedding-package-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-b from-[#C5A059]/20 via-transparent to-transparent" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Heart className="w-10 h-10 text-[#C5A059] mx-auto mb-4" />
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Your Special Day
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Wedding<br />
              <span className="gold-gradient">Packages</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto">
              Let us make your wedding day unforgettable. From bridal hair and makeup to
              your entire wedding party — we bring luxury beauty to your celebration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Location Options */}
      <section className="py-8 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <div className="flex items-center gap-3 text-[#A3A3A3]">
              <Store size={18} className="text-[#C5A059]" />
              <span className="font-body text-sm">Available In-Store</span>
            </div>
            <div className="w-px h-6 bg-[#262626] hidden sm:block" />
            <div className="flex items-center gap-3 text-[#A3A3A3]">
              <Home size={18} className="text-[#C5A059]" />
              <span className="font-body text-sm">Available In-Home (We Come to You)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bridal Code Promo */}
      <section className="py-10 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-[#C5A059]/10 to-transparent border border-[#C5A059]/30 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4">
            <Gift className="w-8 h-8 text-[#C5A059] flex-shrink-0" />
            <div>
              <h3 className="font-heading text-lg text-white mb-1">Exclusive Bridal Code</h3>
              <p className="font-body text-sm text-[#A3A3A3]">
                When you book a wedding package, you'll receive a <strong className="text-[#C5A059]">special bridal code</strong> to
                share with your wedding party. They get <strong className="text-white">10% off all services</strong> at Luxury Beauty!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hair & Makeup Combo Packages */}
      <section className="py-12 md:py-16 bg-[#0A0A0A]" data-testid="combo-packages">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <Sparkles className="w-6 h-6 text-[#C5A059]" />
            <h2 className="font-heading text-xl md:text-2xl text-white">Bridal Hair & Makeup Packages</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {comboPackages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} onInquire={openInquiry} />
            ))}
          </div>
        </div>
      </section>

      {/* Hair Only */}
      <section className="py-12 md:py-16 bg-[#050505]" data-testid="hair-packages">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <Scissors className="w-6 h-6 text-[#C5A059]" />
            <h2 className="font-heading text-xl md:text-2xl text-white">Just Bridal Hair</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {hairOnlyPackages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} onInquire={openInquiry} />
            ))}
          </div>
        </div>
      </section>

      {/* Makeup Only */}
      <section className="py-12 md:py-16 bg-[#0A0A0A]" data-testid="makeup-packages">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <Eye className="w-6 h-6 text-[#C5A059]" />
            <h2 className="font-heading text-xl md:text-2xl text-white">Just Bridal Makeup</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {makeupOnlyPackages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} onInquire={openInquiry} />
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="wedding-inquiry" className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {submitted && bridalCode ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="bg-[#121212] border border-[#262626] p-8 md:p-12 text-center" data-testid="wedding-inquiry-success">
                <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
                  Inquiry Submitted!
                </h3>
                <p className="font-body text-[#A3A3A3] mb-6">
                  Thank you, {form.bride_name}! We'll contact you at <strong className="text-white">{form.phone}</strong> to discuss your wedding package.
                </p>

                <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#C5A059]/30 p-8 my-8 relative overflow-hidden max-w-sm mx-auto">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A059]/5 rounded-full -mr-16 -mt-16" />
                  <div className="relative text-left">
                    <div className="flex items-center gap-3 mb-3">
                      <Heart className="w-5 h-5 text-[#C5A059]" />
                      <span className="font-heading text-sm text-white">Bridal Discount Code</span>
                    </div>
                    <p className="text-xs text-[#A3A3A3] mb-2">Share with your wedding party for 10% off all services</p>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-2xl text-[#C5A059]" data-testid="bridal-code">{bridalCode}</span>
                      <button onClick={copyCode} className="text-[#C5A059] hover:text-white transition-colors" data-testid="copy-bridal-code">
                        <Copy size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => { setSubmitted(false); setBridalCode(null); setForm({ bride_name: "", partner_name: "", phone: "", email: "", wedding_date: "", party_size: "", message: "" }); setSelectedPackage(""); }}
                    className="text-[#C5A059] text-sm tracking-wider uppercase hover:underline"
                  >
                    Submit Another Inquiry
                  </button>
                  <Link to="/book" className="text-[#A3A3A3] text-sm tracking-wider uppercase hover:text-white">
                    Book a Service
                  </Link>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-8">
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Get In Touch
                </span>
                <h2 className="font-heading text-2xl md:text-3xl text-white mt-2">
                  Wedding Inquiry
                </h2>
                <p className="font-body text-sm text-[#A3A3A3] mt-2">
                  Tell us about your big day and we'll create a custom package for you.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-[#121212] border border-[#262626] p-6 md:p-8 space-y-6" data-testid="wedding-inquiry-form">
                {/* Selected Package */}
                {selectedPackage && (
                  <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 p-4 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-[#C5A059] uppercase tracking-wider">Selected Package</span>
                      <p className="text-white font-semibold">{selectedPackage}</p>
                    </div>
                    <button type="button" onClick={() => setSelectedPackage("")} className="text-[#A3A3A3] text-xs hover:text-white">Change</button>
                  </div>
                )}

                {/* Package Select (if none selected) */}
                {!selectedPackage && (
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Select Package *</label>
                    <select
                      value={selectedPackage}
                      onChange={(e) => setSelectedPackage(e.target.value)}
                      required
                      data-testid="wedding-package-select"
                      className="w-full bg-transparent border-b border-white/20 text-white py-3 focus:border-[#C5A059] focus:outline-none"
                      style={{ borderRadius: 0 }}
                    >
                      <option value="" className="bg-[#121212]">Choose a package...</option>
                      <optgroup label="Hair & Makeup Packages" className="bg-[#121212]">
                        {comboPackages.map(p => <option key={p.id} value={p.title} className="bg-[#121212]">{p.title}</option>)}
                      </optgroup>
                      <optgroup label="Just Hair" className="bg-[#121212]">
                        {hairOnlyPackages.map(p => <option key={p.id} value={p.title} className="bg-[#121212]">{p.title}</option>)}
                      </optgroup>
                      <optgroup label="Just Makeup" className="bg-[#121212]">
                        {makeupOnlyPackages.map(p => <option key={p.id} value={p.title} className="bg-[#121212]">{p.title}</option>)}
                      </optgroup>
                    </select>
                  </div>
                )}

                {/* Service Location */}
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-3">Service Location *</label>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setServiceLocation("in_store")}
                      data-testid="location-in-store"
                      className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs tracking-wider uppercase font-semibold transition-all ${
                        serviceLocation === "in_store" ? "bg-[#C5A059] text-[#050505]" : "bento-card text-[#A3A3A3]"
                      }`}>
                      <Store size={14} /> In-Store
                    </button>
                    <button type="button" onClick={() => setServiceLocation("in_home")}
                      data-testid="location-in-home"
                      className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs tracking-wider uppercase font-semibold transition-all ${
                        serviceLocation === "in_home" ? "bg-[#C5A059] text-[#050505]" : "bento-card text-[#A3A3A3]"
                      }`}>
                      <Home size={14} /> In-Home
                    </button>
                  </div>
                </div>

                {/* Bride & Partner */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Bride's Name *</label>
                    <input type="text" name="bride_name" value={form.bride_name} onChange={handleChange} required
                      data-testid="wedding-bride-name" placeholder="Bride's full name" className="w-full luxury-input" />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Partner's Name</label>
                    <input type="text" name="partner_name" value={form.partner_name} onChange={handleChange}
                      data-testid="wedding-partner-name" placeholder="Partner's name (optional)" className="w-full luxury-input" />
                  </div>
                </div>

                {/* Contact */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Phone *</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} required
                      data-testid="wedding-phone" placeholder="Phone number" className="w-full luxury-input" />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Email</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange}
                      data-testid="wedding-email" placeholder="Email (optional)" className="w-full luxury-input" />
                  </div>
                </div>

                {/* Wedding Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Wedding Date</label>
                    <input type="text" name="wedding_date" value={form.wedding_date} onChange={handleChange}
                      data-testid="wedding-date" placeholder="e.g. June 15, 2026" className="w-full luxury-input" />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Party Size</label>
                    <input type="text" name="party_size" value={form.party_size} onChange={handleChange}
                      data-testid="wedding-party-size" placeholder="How many people need services?" className="w-full luxury-input" />
                  </div>
                </div>

                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Additional Details</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={3}
                    data-testid="wedding-message" placeholder="Tell us about your vision, any special requests, or questions..."
                    className="w-full luxury-input resize-none" />
                </div>

                <button
                  type="submit"
                  disabled={!form.bride_name || !form.phone || !selectedPackage || isSubmitting}
                  data-testid="wedding-submit-btn"
                  className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all disabled:opacity-40 flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-5 h-5" /> Submit Wedding Inquiry</>
                  )}
                </button>
              </form>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default WeddingPackagePage;
